/**
 * signup-funnel v1.1 (2026-09-30, cc_tasks row 297, T332: recordSignupError, a failed
 *   LinkedIn/Google return, see ./oauth-return-error.ts)
 * signup-funnel v1.0 (2026-09-30, cc_tasks row 284, T319)
 *
 * The funnel's "signup started" step (funnel_daily.step = 'signup_started') is a
 * person PRESSING a sign-up control: the email form submitted after it validated,
 * or Continue with Google / LinkedIn pressed. It used to be recorded when a
 * signed-out browser merely ARRIVED at /auth?next=<public page>. Every public page
 * carries a plain <a href="/auth?next=..."> link (the save modal), so crawlers that
 * run JavaScript and do not say "bot" in their user agent followed those links and
 * were counted as sign-up starts: 33 "starts" on 2026-09-29 were 0 people.
 *
 *   signup_start / submit_email | oauth_google | oauth_linkedin   <- counted as started
 *   signup_page  / view                                           <- arrival only, not counted
 *
 * The old arrival rows (action 'start') stay in public_page_events as history;
 * refresh_funnel_daily ignores them (migration 2026_09_30_t319_signup_funnel_honest.sql).
 *
 * Same writer and visitor id as public/page-events.js; the server keeps an event
 * only for a page we publish and drops a bot's view. Nothing is sent for a browser
 * that opted out (DNT / GPC / Essential Only) or is driven by a script.
 */
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/lib/supabaseKeys";
import { acquisition } from "@/lib/acquisition";

export type SignupMethod = "email" | "google" | "linkedin";

function safePath(v: string | null | undefined): string | null {
  const s = String(v || "").split("?")[0].split("#")[0].trim();
  if (!s.startsWith("/") || s.startsWith("//") || s.startsWith("/\\")) return null;
  return s.slice(0, 300);
}

function restricted(): boolean {
  try {
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
    if (navigator.doNotTrack === "1" || nav.globalPrivacyControl === true || navigator.webdriver) return true;
    const c = JSON.parse(localStorage.getItem("mentionfox_cookie_consent") || "null");
    if (c?.level === "essential") return true;
  } catch { /* unreadable: not restricted */ }
  return false;
}

/**
 * The public page this sign-up came from: ?next= / ?return_to= on this address, then the
 * stash Auth.tsx and the OAuth helpers keep (30 minutes), then the last call to action
 * page-events.js remembered (a day). Null when none: the home page is used.
 */
export function signupSourcePage(explicit?: string | null): string {
  const ok = (v: string | null | undefined) => {
    const p = safePath(v);
    return p && !/^\/(dashboard|app|settings|auth|admin|clients|onboarding)\b/.test(p) ? p : null;
  };
  const fromExplicit = ok(explicit);
  if (fromExplicit) return fromExplicit;
  try {
    const q = new URLSearchParams(window.location.search);
    const fromUrl = ok(q.get("next") || q.get("return_to"));
    if (fromUrl) return fromUrl;
  } catch { /* ignore */ }
  try {
    const at = Number(localStorage.getItem("mf_next_at") || "0");
    if (at > 0 && Date.now() - at < 30 * 60 * 1000) {
      const p = ok(localStorage.getItem("mf_next"));
      if (p) return p;
    }
  } catch { /* ignore */ }
  try {
    const cta = JSON.parse(localStorage.getItem("mf_last_cta") || "null");
    if (cta?.page && Date.now() - Date.parse(cta.at || "") < 24 * 3600 * 1000) {
      const p = ok(cta.page);
      if (p) return p;
    }
  } catch { /* ignore */ }
  return "/";
}

function send(element: string, action: string, page: string): void {
  if (restricted()) return;
  try {
    let sid: string | null = null;
    try { sid = localStorage.getItem("mf_evt_session"); } catch { sid = null; }
    void fetch(`${SUPABASE_URL}/functions/v1/run-service/page_event`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", apikey: SUPABASE_PUBLISHABLE_KEY },
      body: JSON.stringify({
        page_url: `https://mentionfox.com${page}`,
        element,
        action,
        session_id: sid,
        acq: acquisition(),
      }),
    }).catch(() => undefined);
  } catch { /* never let counting break a sign-up */ }
}

/** A sign-up control was pressed. Once per method per page per browser tab session. */
export function recordSignupStart(method: SignupMethod, fromPage?: string | null): void {
  try {
    const page = signupSourcePage(fromPage);
    const key = `mf_signup_start2:${method}:${page}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* no storage: still count this press */ }
    send("signup_start", method === "email" ? "submit_email" : `oauth_${method}`, page);
  } catch { /* not counted */ }
}

/**
 * v1.1 (2026-09-30, cc_tasks row 297, T332): a sign-in with LinkedIn or Google came back
 * with an error. Element signup_error, action "<provider>:<error code>" (for example
 * "linkedin:access_denied"). Only the auth server's fixed error code is sent, never its
 * description and never anything about the person. Once per code per tab session.
 */
export function recordSignupError(provider: "linkedin" | "google" | "unknown", code: string, fromPage?: string | null): void {
  try {
    const c = String(code || "").toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 40) || "unknown";
    const page = signupSourcePage(fromPage);
    const key = `mf_signup_error:${provider}:${c}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* no storage: still count it */ }
    send("signup_error", `${provider}:${c}`, page);
  } catch { /* not counted */ }
}

/** A signed-out browser arrived at the sign-up page from a public page. Not a start. */
export function recordSignupPageView(): void {
  try {
    const q = new URLSearchParams(window.location.search);
    const next = safePath(q.get("next") || q.get("return_to"));
    if (!next) return;
    const page = signupSourcePage(next);
    if (page === "/") return;
    const key = `mf_signup_page:${page}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* ignore */ }
    send("signup_page", "view", page);
  } catch { /* not counted */ }
}
